import React, { useMemo, useState } from 'react';

import luxuryEscapesLogo from '~/assets/logo.svg';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import TranslationCatalogStatusChip from './TranslationCatalogStatusChip';
import { getTranslationLocales, isTranslationCatalogLocked } from './translationCatalogUtils';
import type { TranslationCatalog } from './types';

interface Props {
  catalogs: Array<TranslationCatalog>;
  selectedCatalogId?: string;
  isLoading: boolean;
  onSelectCatalog: (catalogId: string) => void;
}

type ChipCategory = 'labels' | 'locales' | 'open' | 'locked';

const CHIP_CATEGORY_COLORS: Record<ChipCategory, { main: string; bg: string; border: string }> = {
  labels: { main: '#5B4B8A', bg: '#EFE9FF', border: '#B8A5EA' },
  locales: { main: '#006C8F', bg: '#E0F7FF', border: '#8BD3EA' },
  open: { main: '#52616B', bg: '#F1F4F6', border: '#C6D0D7' },
  locked: { main: '#1B6E3C', bg: '#DFF4E7', border: '#80C99B' },
};

function getChipSx(category: ChipCategory) {
  const colors = CHIP_CATEGORY_COLORS[category];
  return {
    bgcolor: colors.bg,
    color: colors.main,
    borderColor: colors.border,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: colors.main,
    },
  };
}

const brandChipSx = {
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const lockedIconChipSx = {
  ...getChipSx('locked'),
  width: 28,
  '& .MuiChip-label': {
    display: 'none',
  },
  '& .MuiChip-icon': {
    mx: 0,
    color: CHIP_CATEGORY_COLORS.locked.main,
  },
};

export default function TranslationCatalogList({ catalogs, selectedCatalogId, isLoading, onSelectCatalog }: Props) {
  const [search, setSearch] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const filteredCatalogs = useMemo(
    () =>
      catalogs.filter((catalog) => {
        const query = search.trim().toLowerCase();
        if (!query) return true;
        return [catalog.catalogName, catalog.brand, catalog.status, catalog.id].join(' ').toLowerCase().includes(query);
      }),
    [catalogs, search],
  );

  if (isCollapsed) {
    return (
      <Stack
        sx={{
          width: { xs: '100%', xl: 48 },
          height: { xs: 48, xl: 'calc(100vh - 210px)' },
          minHeight: { xl: 520 },
          alignItems: 'center',
          justifyContent: { xs: 'center', xl: 'flex-start' },
          py: { xl: 1 },
          px: { xs: 1, xl: 0 },
          borderRight: { xl: 1 },
          borderBottom: { xs: 1, xl: 0 },
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack
          direction={{ xs: 'row', xl: 'column' }}
          alignItems="center"
          justifyContent="flex-start"
          gap={1}
          sx={{ width: '100%', height: '100%' }}
        >
          <Tooltip title="Show catalogs" placement="right">
            <IconButton aria-label="Show catalogs" onClick={() => setIsCollapsed(false)} size="small">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Stack direction={{ xs: 'row', xl: 'column' }} alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0.75,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <FolderOutlinedIcon fontSize="small" color="primary" />
            </Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ writingMode: { xl: 'vertical-rl' }, transform: { xl: 'rotate(180deg)' } }}
            >
              Catalogs
            </Typography>
            <Chip label={catalogs.length} size="small" sx={{ height: 20, minWidth: 28 }} />
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: { xs: '100%', xl: 'clamp(180px, 10vw, 240px)' },
        height: { xs: 'auto', xl: 'calc(100vh - 210px)' },
        minHeight: { xl: 520 },
        borderRight: { xl: 1 },
        borderBottom: { xs: 1, xl: 0 },
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Catalogs
        </Typography>
        <Tooltip title="Hide catalogs">
          <IconButton aria-label="Hide catalogs" onClick={() => setIsCollapsed(true)} size="small">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ px: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search catalogs"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isLoading && <LinearProgress />}
      <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
        {filteredCatalogs.map((catalog) => {
          const locales = getTranslationLocales(catalog);
          const isSelected = catalog.id === selectedCatalogId;
          const isLocked = isTranslationCatalogLocked(catalog);
          const shouldShowLockState = catalog.status !== 'Locked';

          return (
            <ListItemButton
              key={catalog.id}
              selected={isSelected}
              onClick={() => onSelectCatalog(catalog.id)}
              aria-label={`${catalog.catalogName} catalog`}
              sx={{
                alignItems: 'flex-start',
                borderBottom: 1,
                borderColor: 'divider',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Stack spacing={0.75} minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {catalog.catalogName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {catalog.id}
                </Typography>
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  <Tooltip title={catalog.brand}>
                    <Box aria-label={catalog.brand} role="img" sx={brandChipSx}>
                      <Box
                        component="img"
                        src={luxuryEscapesLogo}
                        alt=""
                        sx={{
                          width: 20,
                          height: 20,
                          display: 'block',
                          filter: 'invert(1)',
                        }}
                      />
                    </Box>
                  </Tooltip>
                  {catalog.status === 'Locked' ? (
                    <Chip
                      icon={<LockOutlinedIcon />}
                      label=""
                      aria-label="Locked"
                      size="small"
                      variant="outlined"
                      sx={lockedIconChipSx}
                    />
                  ) : (
                    <TranslationCatalogStatusChip status={catalog.status} />
                  )}
                  <Chip
                    icon={<LocalOfferOutlinedIcon />}
                    label={`${catalog.columnNames.length} labels`}
                    size="small"
                    variant="outlined"
                    sx={getChipSx('labels')}
                  />
                  <Chip
                    icon={<LanguageIcon />}
                    label={`${locales.length} locales`}
                    size="small"
                    variant="outlined"
                    sx={getChipSx('locales')}
                  />
                  {shouldShowLockState && (
                    <Chip
                      icon={isLocked ? <LockOutlinedIcon /> : undefined}
                      label={isLocked ? 'Locked' : 'Open'}
                      size="small"
                      variant="outlined"
                      sx={getChipSx(isLocked ? 'locked' : 'open')}
                    />
                  )}
                </Stack>
              </Stack>
            </ListItemButton>
          );
        })}
        {!isLoading && filteredCatalogs.length === 0 && (
          <Box sx={{ px: 1.5, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No catalogs found.
            </Typography>
          </Box>
        )}
      </List>
    </Stack>
  );
}
